// HT/FT — types + pure counting (Phase 3).
//
// For one team's recent matches, how the half-time result (W/D/L) turned into
// the full-time result — shown as a flow diagram (HT nodes → FT nodes, ribbons
// = transition counts) or a bar view (W/D/L split at HT and FT). Both views read
// the same counts; the layout + D3 animation live in the components.

import type { FormMatch } from "./form.js";

export type HtftResult = "W" | "D" | "L";

/** W/D/L counts. */
export type HtftCounts = Record<HtftResult, number>;

/** The counting result: HT + FT distributions, and the HT→FT flow counts. */
export interface HtftCountData {
  htC: HtftCounts;
  ftC: HtftCounts;
  /** Flow counts keyed "HT/FT", e.g. "W/W", "D/L". */
  flows: Record<string, number>;
}

/** Display order of the three outcomes. */
export const HTFT_ORDER: readonly HtftResult[] = ["W", "D", "L"] as const;

/**
 * The team's result in a match, from the HT (`useHt`) or FT score. Home/away
 * aware: the away team's W/L is the mirror of the raw score comparison.
 */
export function htftResult(
  m: FormMatch,
  team: string,
  useHt: boolean
): HtftResult {
  const g = (useHt ? m.ht : m.score).split("–").map(Number);
  const [h, a] = g as [number, number];
  const isHome = m.home === team;
  if (h === a) return "D";
  const homeWon = h > a;
  return (isHome ? homeWon : !homeWon) ? "W" : "L";
}

/** Count HT + FT distributions and HT→FT flows across the matches. */
export function htftCounts(
  matches: readonly FormMatch[],
  team: string
): HtftCountData {
  const htC: HtftCounts = { W: 0, D: 0, L: 0 };
  const ftC: HtftCounts = { W: 0, D: 0, L: 0 };
  const flows: Record<string, number> = {};
  for (const m of matches) {
    const ht = htftResult(m, team, true);
    const ft = htftResult(m, team, false);
    htC[ht]++;
    ftC[ft]++;
    const key = `${ht}/${ft}`;
    flows[key] = (flows[key] || 0) + 1;
  }
  return { htC, ftC, flows };
}

/**
 * The window of recent matches for the timeline control: last 5, last 10, or
 * all ("Season"). Matches the legacy `slice(-min(N, len))`.
 */
export function selectHtftMatches(
  src: readonly FormMatch[],
  timeline: string | null | undefined
): FormMatch[] {
  const n =
    timeline === "10" ? 10 : timeline === "Season" ? src.length : 5;
  return src.slice(-Math.min(n, src.length));
}
